import { Users, Search, Mail, Phone, Calendar } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { SquadDTO, SquadPessoaDTO } from "@/types/squad.type";
import { useSquadMembers } from "@/hooks/squads/use-squad-members";
import { PersonBadge } from "@/components/shared/PersonBadge";

interface SquadViewMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  squadToView: SquadDTO | null;
}

export function SquadViewMembersDialog({
  open,
  onOpenChange,
  squadToView,
}: SquadViewMembersDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Hook agora só dispara se o modal estiver aberto e houver um ID
  const { squadMembersData, isLoading } = useSquadMembers(squadToView?.id, open);

  // Limpa o filtro sempre que o modal é fechado
  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const filteredMembers = useMemo(() => {
    if (!squadMembersData) return [];
    const term = searchTerm.toLowerCase();
    return squadMembersData.filter(
      (m) =>
        m.pessoa?.nome?.toLowerCase().includes(term) ||
        m.pessoa?.email?.toLowerCase().includes(term),
    );
  }, [squadMembersData, searchTerm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Mudamos h-[80vh] para max-h-[80vh] */}
      <DialogContent className="flex max-h-[80vh] w-full flex-col gap-0 overflow-hidden border-border bg-card p-0 shadow-2xl sm:rounded-xl md:max-w-2xl">
        <DialogHeader className="z-10 shrink-0 border-b border-border bg-card p-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2.5 text-lg text-foreground">
                <div className="rounded-md bg-primary/10 p-1.5">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                Membros da Squad
              </DialogTitle>
              <DialogDescription className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
                Nome: <span className="font-semibold text-foreground/80">{squadToView?.nome}</span>
              </DialogDescription>
            </div>
            {!isLoading && squadMembersData && (
              <Badge variant="secondary" className="mt-1">
                {filteredMembers.length} membros
              </Badge>
            )}
          </div>

          <div className="relative mt-4">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar membros por nome ou email..."
              className="border-border bg-muted/30 pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading || !squadMembersData?.length} // Desabilita se não tem dados
            />
          </div>
        </DialogHeader>

        <div className="min-h-0 w-full flex-1 bg-card/50">
          {/* ScrollArea agora tem max-height interno para não forçar o modal a ficar gigante se tiver pouco conteúdo */}
          <ScrollArea className="h-full max-h-[50vh] w-full">
            <div className="p-4">
              {isLoading ? (
                <div className="flex h-[30vh] animate-pulse flex-col items-center justify-center gap-3 text-muted-foreground">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/30 border-t-primary" />
                  <p className="text-sm font-medium">Carregando membros...</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="flex h-[30vh] flex-col items-center justify-center gap-4 text-muted-foreground opacity-60">
                  <Users className="h-12 w-12" />
                  <p className="text-center font-medium">
                    {searchTerm
                      ? "Nenhum membro corresponde à busca."
                      : "Nenhum membro vinculado a esta squad."}
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredMembers.map((membro) => (
                    <MembroCard key={membro.id} membro={membro} />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <div className="flex shrink-0 justify-end border-t border-border bg-muted/10 p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Subcomponente para deixar o código mais conciso e focado
function MembroCard({ membro }: { membro: SquadPessoaDTO }) {
  return (
    <div className="group flex items-center justify-between rounded-lg border border-border bg-muted/20 p-3 transition-all hover:bg-muted/40">
      <div className="flex items-center gap-4">
        <PersonBadge
          pessoa={membro.pessoa!}
          isCompact={false}
          className="border-none bg-transparent p-0"
        />
        <div className="ml-2 hidden flex-col gap-0.5 md:flex">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Mail className="h-3 w-3" />
            {membro.pessoa?.email || "Sem e-mail"}
          </div>
          {membro.criadoEm && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              Adicionado à squad em {format(new Date(membro.criadoEm), "dd/MM/yyyy")}
            </div>
          )}
        </div>
      </div>

      <Badge
        variant="outline"
        className={
          membro.ativo
            ? "border-emerald-100 bg-emerald-50 text-emerald-700"
            : "border-rose-100 bg-rose-50 text-rose-700"
        }
      >
        {membro.ativo ? "Ativo" : "Inativo"}
      </Badge>
    </div>
  );
}
